import { useCallback, useEffect } from 'react';
import { FilterValue } from '../types/index';
import { FILTER_VALIDATION } from '../constants/index';
import {
  decodeData,
  encodeFiltersToParams,
  decodeFiltersFromParams,
} from '../../../utils/encodeUtils';
import Filters from '../Filters';

export const useFilterManagement = (
  defaultFilterValue: FilterValue[],
  setDefaultFilterValue: (filters: FilterValue[]) => void,
  isEvidence: boolean,
  filtersLoadedFromURL: boolean,
  setFiltersLoadedFromURL: (loaded: boolean) => void,
  gridContainerRef: React.RefObject<HTMLDivElement>,
  setURLWithFilters: (url: string) => void,
  setPendingFiltersToSave: (filters: FilterValue[] | null) => void,
  setSkip: (skip: number) => void
) => {
  const checkIncompleteFilters = useCallback((filterValue: FilterValue[]) => {
    const incompleteFilters: string[] = [];

    filterValue.forEach(filter => {
      if (filter.name === 'reference' && filter.value && filter.value.length < FILTER_VALIDATION.REFERENCE_MIN_LENGTH) {
        incompleteFilters.push('reference');
      }
      if (filter.name === 'pmid' && filter.value && filter.value.length < FILTER_VALIDATION.PMID_MIN_LENGTH) {
        incompleteFilters.push('PMID');
      }
    });

    if (incompleteFilters.length > 0) {
      const filterNames = incompleteFilters.join(' y ');
      return `You must write the full ${filterNames} to filter`;
    }

    return null;
  }, []);

  const onFilterValueChange = useCallback(
    (value: FilterValue[]) => {
      const scrollX = gridContainerRef.current?.scrollLeft || 0;
      setDefaultFilterValue(value);

      const filters = value.filter(v => v.value);
      const filtersWithEvidence = filters.map(filter => ({ ...filter, isEvidence }));

      setPendingFiltersToSave(filtersWithEvidence);

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('f');

      const tablePrefix = isEvidence ? 'evidence' : 'summary';
      const keysToDelete: string[] = [];
      for (const key of currentUrl.searchParams.keys()) {
        if (key.startsWith(`filters-${tablePrefix}-`)) {
          keysToDelete.push(key);
        }
        if (key.startsWith(`filters[${tablePrefix}][`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => currentUrl.searchParams.delete(key));

      if (filtersWithEvidence.length > 0) {
        const filterParams = encodeFiltersToParams(filtersWithEvidence);
        for (const [key, value] of filterParams.entries()) {
          currentUrl.searchParams.set(key, value);
        }
      }

      window.history.replaceState({}, '', currentUrl.href);
      setURLWithFilters(currentUrl.href);

      setTimeout(() => {
        if (gridContainerRef.current) gridContainerRef.current.scrollLeft = scrollX;
      }, 0);
    },
    [isEvidence, gridContainerRef, setDefaultFilterValue, setPendingFiltersToSave, setURLWithFilters]
  );

  const handleClearAllFilters = useCallback(() => {
    const baseFilters = Filters.filter((f: any) => f).map((f: any) => ({ ...f, value: '' }));

    setSkip(0);

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('f');

      const tablePrefix = isEvidence ? 'evidence' : 'summary';
      const keysToDelete: string[] = [];
      for (const key of url.searchParams.keys()) {
        if (key.startsWith(`filters-${tablePrefix}-`)) {
          keysToDelete.push(key);
        }
        if (key.startsWith(`filters[${tablePrefix}][`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => url.searchParams.delete(key));

      window.history.replaceState({}, '', url);
      setURLWithFilters('');
    } catch (e) {
      // ignore URL errors
    }

    setDefaultFilterValue(baseFilters);
    onFilterValueChange(baseFilters);
  }, [onFilterValueChange, isEvidence, setSkip, setURLWithFilters, setDefaultFilterValue]);

  const handleRemoveFilter = useCallback(
    (filterToRemove: FilterValue) => {
      const newFilters = defaultFilterValue.map(f => {
        if (
          f.name === filterToRemove.name &&
          (f.type !== 'number' || f.operator === filterToRemove.operator)
        ) {
          return { ...f, value: '' };
        }
        return f;
      });

      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange, setDefaultFilterValue]
  );

  const handleFilterChange = useCallback(
    (columnId: string, value: string) => {
      const newFilters = defaultFilterValue.map(f => (f.name === columnId ? { ...f, value } : f));
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange]
  );

  const handleFilterOperatorChange = useCallback(
    (columnId: string, operator: string) => {
      const newFilters = defaultFilterValue.map(f => (f.name === columnId ? { ...f, operator } : f));
      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange, setDefaultFilterValue]
  );

  // Load filters from URL on mount
  useEffect(() => {
    if (filtersLoadedFromURL) return;

    const p = new URLSearchParams(location.search);
    let decodedFilters = decodeFiltersFromParams(p);

    if (decodedFilters.length === 0) {
      const filterToken = p.get('f');
      if (filterToken) {
        try {
          decodedFilters = decodeData(filterToken);
        } catch (error) {
          // Ignore decode errors
        }
      }
    }

    if (decodedFilters?.length) {
      const relevantFilters = decodedFilters.filter((f: FilterValue) => f.isEvidence === isEvidence);

      if (relevantFilters.length > 0) {
        const newFilters = (() => {
          const baseFiltersMap = new Map(Filters.filter((f: any) => f).map((f: any) => [f.name, { ...f, value: f.value || '' }]));

          relevantFilters.forEach((f: FilterValue) => {
            const baseFilter = Filters.find((bf: any) => bf.name === f.name);
            if (baseFilter) {
              baseFiltersMap.set(f.name, {
                ...baseFilter,
                ...f,
                type: baseFilter.type,
                integer: baseFilter.integer,
                value: f.value || '',
              });
            } else {
              baseFiltersMap.set(f.name, { ...f, value: f.value || '' });
            }
          });

          return Array.from(baseFiltersMap.values());
        })();
        
        setDefaultFilterValue(newFilters);

        setFiltersLoadedFromURL(true);
      }
    } else {
      setFiltersLoadedFromURL(true);
    }
  }, [location.search, isEvidence, filtersLoadedFromURL, setDefaultFilterValue, setFiltersLoadedFromURL]);

  return {
    checkIncompleteFilters,
    onFilterValueChange,
    handleClearAllFilters,
    handleRemoveFilter,
    handleFilterChange,
    handleFilterOperatorChange,
  };
};